import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DashboardSkeleton from "./DashboardSkeleton";
import StudentProfileModal from "./StudentProfileModal";
import ChangePasswordModal from "./ChangePasswordModal";

import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Treemap,
} from "recharts";
import AISearch from "./AISearch";

const EmptyState = ({ title, message }) => (
  <div className="flex flex-col h-full min-h-[140px] items-center justify-center text-center p-6 bg-slate-900/30 rounded-xl border border-dashed border-slate-800/60 mt-2">
    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900/80 mb-3 text-slate-500 shadow-inner">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m16 16-4-4V8"/></svg>
    </div>
    <h3 className="text-sm font-medium text-slate-300">{title}</h3>
    <p className="mt-1 text-xs text-slate-500 max-w-[200px]">{message}</p>
  </div>
);

// ─── Consistent color palette for subjects ───────────────────────────────────//
const COLOR_PALETTE = [
  "#6366f1", "#0ea5e9", "#22c55e", "#eab308", "#f97316", "#f43f5e",
  "#8b5cf6", "#14b8a6", "#ef4444", "#a855f7", "#06b6d4", "#84cc16",
];

const FILTER_MODES = [
  { key: "hour", label: "Day" },
  { key: "day", label: "Week" },
  { key: "week", label: "Month" },
  { key: "month", label: "Year" },
];

const GRID_FILTERS = [
  { key: "all", label: "All" },
  { key: "best10", label: "Best 10%" },
  { key: "worst10", label: "Worst 10%" },
];

const FILTER_MODE_LEVELS = {
  hour: 0,
  day: 1,
  week: 2,
  month: 3,
};

// ─── Date helpers (all UTC to match dayStartS in database) ──────────────────
function startOfDay(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function startOfWeek(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay();
  const diff = d.getUTCDate() - day + (day === 0 ? -6 : 1);
  d.setUTCDate(diff);
  return d;
}

function startOfMonth(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function startOfYear(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
}

function addDays(date, n) {
  const d = new Date(date);
  d.setUTCDate(d.getUTCDate() + n);
  return d;
}

function addMonths(date, n) {
  const d = new Date(date);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d;
}

function addYears(date, n) {
  const d = new Date(date);
  d.setUTCFullYear(d.getUTCFullYear() + n);
  return d;
}

function getDateRange(filterMode, refDate) {
  let start, end;
  switch (filterMode) {
    case "hour":
      start = startOfDay(refDate);
      end = addDays(start, 1);
      break;
    case "day":
      start = startOfWeek(refDate);
      end = addDays(start, 7);
      break;
    case "week":
      start = startOfMonth(refDate);
      end = addMonths(start, 1);
      break;
    case "month":
      start = startOfYear(refDate);
      end = addYears(start, 1);
      break;
    default:
      start = new Date(0);
      end = new Date();
  }
  return {
    startEpoch: Math.floor(start.getTime() / 1000),
    endEpoch: Math.floor(end.getTime() / 1000),
  };
}

function getDateBounds(filterMode, refDate) {
  const { startEpoch, endEpoch } = getDateRange(filterMode, refDate);
  return {
    start: new Date(startEpoch * 1000),
    end: new Date(endEpoch * 1000),
  };
}

function formatUTCDate(date, options) {
  return date.toLocaleDateString("en-US", { ...options, timeZone: "UTC" });
}

function getNavigationLabel(filterMode, refDate) {
  switch (filterMode) {
    case "hour":
      return formatUTCDate(refDate, { month: "short", day: "numeric", year: "numeric" });
    case "day": {
      const ws = startOfWeek(refDate);
      const we = addDays(ws, 6);
      return `${formatUTCDate(ws, { month: "short", day: "numeric" })} – ${formatUTCDate(we, { month: "short", day: "numeric", year: "numeric" })}`;
    }
    case "week":
      return formatUTCDate(refDate, { month: "long", year: "numeric" });
    case "month":
      return refDate.getUTCFullYear().toString();
    default:
      return "";
  }
}

function shiftDate(filterMode, refDate, direction) {
  const delta = direction === "next" ? 1 : -1;
  switch (filterMode) {
    case "hour": return addDays(refDate, delta);
    case "day": return addDays(refDate, delta * 7);
    case "week": return addMonths(refDate, delta);
    case "month": return addYears(refDate, delta);
    default: return refDate;
  }
}

function getPeriodStart(filterMode, refDate) {
  switch (filterMode) {
    case "hour":
      return startOfDay(refDate);
    case "day":
      return startOfWeek(refDate);
    case "week":
      return startOfMonth(refDate);
    case "month":
      return startOfYear(refDate);
    default:
      return new Date(refDate);
  }
}

function canNavigateDate(filterMode, refDate, direction) {
  if (direction !== "next") return true;
  const nextRefDate = shiftDate(filterMode, refDate, direction);
  const nextPeriodStart = getPeriodStart(filterMode, nextRefDate);
  const todayPeriodStart = getPeriodStart(filterMode, new Date());
  return nextPeriodStart <= todayPeriodStart;
}

function navigateDate(filterMode, refDate, direction) {
  if (!canNavigateDate(filterMode, refDate, direction)) {
    return refDate;
  }
  return shiftDate(filterMode, refDate, direction);
}

function getLabels(filterMode, refDate) {
  switch (filterMode) {
    case "hour":
      return Array.from({ length: 24 }, (_, i) => `${i}:00`);
    case "day": {
      const weekStart = startOfWeek(refDate);
      return Array.from({ length: 7 }, (_, i) =>
        formatUTCDate(addDays(weekStart, i), { month: "short", day: "numeric" })
      );
    }
    case "week":
      return ["Week 1", "Week 2", "Week 3", "Week 4"];
    case "month":
      return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    default:
      return [];
  }
}

function hasDataInBounds(data, start, end) {
  const startEpoch = Math.floor(start.getTime() / 1000);
  const endEpoch = Math.floor(end.getTime() / 1000);
  return data.some((item) => {
    const dayStart = item.dayStartS || 0;
    return dayStart >= startEpoch && dayStart < endEpoch;
  });
}

function findClosestDateWithData(data, start, end, anchorDate) {
  const startEpoch = Math.floor(start.getTime() / 1000);
  const endEpoch = Math.floor(end.getTime() / 1000);
  const anchorMs = anchorDate.getTime();

  const candidates = data
    .map((item) => item.dayStartS || 0)
    .filter((dayStart) => dayStart >= startEpoch && dayStart < endEpoch)
    .sort((a, b) => Math.abs(a * 1000 - anchorMs) - Math.abs(b * 1000 - anchorMs));

  return candidates.length > 0 ? new Date(candidates[0] * 1000) : null;
}

function findLatestDateWithData(data) {
  const latestDayStart = data.reduce((latest, item) => {
    const dayStart = item.dayStartS || 0;
    return dayStart > latest ? dayStart : latest;
  }, 0);

  return latestDayStart > 0 ? new Date(latestDayStart * 1000) : null;
}

function resolveRefDateForModeChange(currentMode, nextMode, refDate, data) {
  const currentLevel = FILTER_MODE_LEVELS[currentMode];
  const nextLevel = FILTER_MODE_LEVELS[nextMode];

  if (data.length === 0 || nextLevel >= currentLevel) {
    return refDate;
  }

  const nextBounds = getDateBounds(nextMode, refDate);
  if (hasDataInBounds(data, nextBounds.start, nextBounds.end)) {
    return refDate;
  }

  const currentBounds = getDateBounds(currentMode, refDate);
  return findClosestDateWithData(data, currentBounds.start, currentBounds.end, refDate) || refDate;
}

function getBucketIndex(item, filterMode) {
  const d = new Date((item.dayStartS || 0) * 1000);
  switch (filterMode) {
    case "hour":
      return d.getUTCHours();
    case "day": {
      const day = d.getUTCDay();
      return day === 0 ? 6 : day - 1; // 0=Mon .. 6=Sun
    }
    case "week":
      return Math.min(Math.floor((d.getUTCDate() - 1) / 7), 3);
    case "month":
      return d.getUTCMonth();
    default:
      return 0;
  }
}

function buildSubjectColorMap(data) {
  const subjects = [
    ...new Set(data.filter((d) => d.subject && d.minutesUsed > 0).map((d) => d.subject)),
  ].sort();
  const map = {};
  subjects.forEach((s, i) => {
    map[s] = COLOR_PALETTE[i % COLOR_PALETTE.length];
  });
  return map;
}

function getChapterName(studentClass, subject, code, subjectData) {
  if (!code || !subjectData || !subjectData.chapters) return code;
  const upperCode = String(code).toUpperCase().trim();

  // Try exact class match first
  if (studentClass) {
    const exactKey = `${studentClass}_${subject}`;
    if (subjectData.chapters[exactKey]) {
      const match = subjectData.chapters[exactKey].find((c) => c.code && String(c.code).toUpperCase().trim() === upperCode);
      if (match) return match.name;
    }
  }

  // Optimization Fallback: match inside the specific subject
  for (const key in subjectData.chapters) {
    if (key.endsWith(`_${subject}`)) {
      const match = subjectData.chapters[key].find((c) => c.code && String(c.code).toUpperCase().trim() === upperCode);
      if (match) return match.name;
    }
  }

  // Fallback: search everywhere across all classes/subjects just in case
  for (const key in subjectData.chapters) {
    const match = subjectData.chapters[key].find((c) => c.code && String(c.code).toUpperCase().trim() === upperCode);
    if (match) return match.name;
  }

  return code; // Fallback to raw code
}

const TOOLTIP_STYLE = {
  backgroundColor: "#020617",
  borderRadius: 12,
  border: "1px solid #1f2937",
  padding: 10,
  color: "#e5e7eb",
};

function CustomBarTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ margin: "0 0 6px", fontWeight: 600, fontSize: 13 }}>{label}</p>
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ margin: "2px 0", fontSize: 12, color: entry.color }}>
          {entry.dataKey}: {entry.value} min
        </p>
      ))}
    </div>
  );
}

function CustomPieTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) return null;
  const { name, value, chapters } = payload[0].payload;
  const total = payload[0]?.payload?.total || value;
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ margin: 0, fontWeight: 600, fontSize: 13, color: payload[0]?.payload?.fill || "#e5e7eb" }}>
        {name}
      </p>
      <p style={{ margin: "4px 0 6px", fontSize: 12, borderBottom: "1px solid #374151", paddingBottom: "4px" }}>
        {value} min ({pct}%)
      </p>
      {chapters && Object.keys(chapters).length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {Object.entries(chapters)
            .filter(([_, cValue]) => cValue > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([cName, cValue]) => (
              <p key={cName} style={{ margin: 0, fontSize: 11, color: "#9ca3af", display: "flex", justifyContent: "space-between" }}>
                <span>{cName}:</span> <span style={{ marginLeft: "12px", color: "#d1d5db" }}>{cValue}m</span>
              </p>
            ))}
        </div>
      )}
    </div>
  );
}

function GridSegmentTooltip({ subjects, totalTime }) {
  return (
    <div style={{ ...TOOLTIP_STYLE, position: "absolute", bottom: "calc(100% + 8px)", left: "50%", transform: "translateX(-50%)", whiteSpace: "nowrap", zIndex: 50, pointerEvents: "none" }}>
      <p style={{ margin: "0 0 6px", fontWeight: 600, fontSize: 13 }}>{totalTime} min total</p>
      {subjects.map((sub) => (
        <p key={sub.name} style={{ margin: "2px 0", fontSize: 12, color: sub.color }}>
          {sub.name}: {sub.time} min
        </p>
      ))}
    </div>
  );
}

function CustomTreemapTooltip({ active, payload, colorMap }) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0].payload;

  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ margin: "0 0 4px", fontSize: 11, color: "#9ca3af", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {data.subject}
      </p>
      <p style={{ margin: "0 0 6px", fontWeight: 600, fontSize: 13, color: colorMap[data.subject] || "#e5e7eb" }}>
        {data.name}
      </p>
      <p style={{ margin: 0, fontSize: 12 }}>
        {data.value} min studied
      </p>
    </div>
  );
}

const TreemapContent = (props) => {
  const { depth, x, y, width, height, name, subject, colorMap } = props;
  if (depth !== 2) return null; // chapters are depth===2

  const bgColor = colorMap[subject] || "#3b82f6";
  const showText = width > 40 && height > 25;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={bgColor}
        stroke="#0f172a"
        strokeWidth={2}
        style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: 0.85 }}
      />
      {showText && (
        <text
          x={x + 6}
          y={y + 18}
          fill="#ffffff"
          fontSize={11}
          fontWeight={600}
          style={{ pointerEvents: 'none' }}
        >
          {name.length > 20 ? name.substring(0, 20) + '...' : name}
        </text>
      )}
    </g>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
export default function Dashboard() {
  const [data, setData] = useState(null);
  const [allData, setAllData] = useState(null);
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subjectData, setSubjectData] = useState(null);

  const [filterMode, setFilterMode] = useState("day");
  const filterBarRef = useRef(null);
  const accountMenuRef = useRef(null);
  const calendarScrollRef = useRef(null);
  const hasInitializedRefDate = useRef(false);
  const [refDate, setRefDate] = useState(new Date());

  const [selectedStudentId, setSelectedStudentId] = useState("all");
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [gridFilter, setGridFilter] = useState("all");
  const [hoveredGridStudent, setHoveredGridStudent] = useState(null);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [modalStudentId, setModalStudentId] = useState(null);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Trigger password change modal from query param
  useEffect(() => {
    if (searchParams.get('changePassword') === 'true') {
      setIsChangePasswordOpen(true);
      // Clean up the URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('changePassword');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const penId = user?.penId;
  const isTeacher = user?.role === "institute";

  // ─── Data fetching ───
  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const headers = { 
        'X-Tenant-ID': user?.tenantEmail || user?.email || 'PENSENSE_DEFAULT',
        'Authorization': `Bearer ${user?.token}`
      };

      if (isTeacher) {
        const teacherParam = user?.email ? `?instituteEmail=${encodeURIComponent(user.email)}` : "";
        const [studentsRes, allDataRes] = await Promise.all([
          fetch(`/api/dashboard/students/list${teacherParam}`, { headers }),
          fetch(`/api/dashboard/all-data${teacherParam}`, { headers }),
        ]);

        if (studentsRes.status === 401 || allDataRes.status === 401) {
          localStorage.removeItem("user");
          window.location.href = "/";
          return;
        }

        const studentsList = await studentsRes.json();
        const allStudentsData = await allDataRes.json();
        
        setStudents(Array.isArray(studentsList) ? studentsList : []);
        setAllData(Array.isArray(allStudentsData) ? allStudentsData : []);
        setData(Array.isArray(allStudentsData) ? allStudentsData : []);
      } else {
        const res = await fetch(`/api/dashboard/${penId}?filter=all`, { headers });
        if (res.status === 401) {
          localStorage.removeItem("user");
          window.location.href = "/";
          return;
        }
        const json = await res.json();
        setData(Array.isArray(json) ? json : []);
      }

      try {
        const metadataRes = await fetch("/subject_data.txt");
        if (metadataRes.ok) {
          const metadata = await metadataRes.json();
          setSubjectData(metadata);
        } else {
          console.warn("Got non-ok status from subject metadata:", metadataRes.status);
        }
      } catch (err) {
        console.error("Failed to fetch subject metadata:", err);
      }

    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [isTeacher, user?.email, user?.tenantEmail, user?.token, penId]);

  useEffect(() => {
    if (penId || isTeacher) load();
    else setIsLoading(false);
  }, [penId, isTeacher, load]);

  useEffect(() => {
    function handleDocumentClick(event) {
      if (!accountMenuRef.current?.contains(event.target)) {
        setIsAccountMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, []);

  // ─── Batch filtering logic ───
  const uniqueBatches = useMemo(() => {
    if (!Array.isArray(students)) return ["all"];
    return ["all", ...new Set(students.map(s => s.batch).filter(Boolean))];
  }, [students]);

  const filteredStudentsList = useMemo(() => {
    if (selectedBatch === "all") return students;
    return students.filter(s => s.batch === selectedBatch);
  }, [students, selectedBatch]);

  // If the batch changes and the selected student is no longer in it, reset student
  useEffect(() => {
    if (selectedBatch !== "all" && selectedStudentId !== "all" && Array.isArray(filteredStudentsList)) {
      const studentInBatch = filteredStudentsList.some(s => s.penId === selectedStudentId);
      if (!studentInBatch) {
        setSelectedStudentId("all");
      }
    }
  }, [selectedBatch, selectedStudentId, filteredStudentsList]);

  // ─── Active data based on student selection ───
  const activeData = useMemo(() => {
    if (!data && !allData) return [];
    
    // If a specific student is selected, filter just for them
    if (isTeacher && selectedStudentId !== "all") {
      return (allData || data || []).filter((d) => d.penId === selectedStudentId && d.minutesUsed > 0);
    }
    
    // If "All Students" is selected, but a specific batch is active, filter for that batch
    if (isTeacher && selectedStudentId === "all" && selectedBatch !== "all") {
      const batchPenIds = new Set(filteredStudentsList.map(s => s.penId));
      return (allData || data || []).filter((d) => batchPenIds.has(d.penId) && d.minutesUsed > 0);
    }
    
    // Otherwise show all data (either institute-wide or single student user)
    return (isTeacher ? (allData || data || []) : (data || [])).filter((item) => item.minutesUsed > 0);
  }, [data, allData, isTeacher, selectedStudentId, selectedBatch, filteredStudentsList]);

  useEffect(() => {
    if (hasInitializedRefDate.current || activeData.length === 0) return;

    const latestDateWithData = findLatestDateWithData(activeData);
    if (!latestDateWithData) return;

    setRefDate(latestDateWithData);
    hasInitializedRefDate.current = true;
  }, [activeData]);

  // ─── Date range filtering ───
  const { startEpoch, endEpoch } = useMemo(
    () => getDateRange(filterMode, refDate),
    [filterMode, refDate]
  );

  const filteredData = useMemo(() => {
    return activeData.filter((item) => {
      const ds = item.dayStartS || 0;
      if (ds === 0) return false;
      return ds >= startEpoch && ds < endEpoch;
    });
  }, [activeData, startEpoch, endEpoch]);

  // ─── Subject color map (built from ALL data for consistency) ───
  const subjectColorMap = useMemo(
    () => buildSubjectColorMap(allData || data || []),
    [allData, data]
  );
  const subjects = useMemo(() => Object.keys(subjectColorMap), [subjectColorMap]);

  // ─── Stats ───
  const stats = useMemo(() => {
    const totalMinutes = filteredData.reduce((sum, d) => sum + (d.minutesUsed || 0), 0);
    const subjectSet = new Set(filteredData.map((d) => d.subject).filter(Boolean));

    if (isTeacher && selectedStudentId === "all" && filteredStudentsList.length > 0) {
      const n = filteredStudentsList.length;
      return {
        totalStudy: Math.round(totalMinutes / n),
        subjectsCovered: subjectSet.size,
        avgMinPerSubject: subjectSet.size === 0 ? 0 : Math.round(totalMinutes / n / subjectSet.size),
        label: selectedBatch === "all" ? "Average per student" : `Average per student in Batch ${selectedBatch}`,
      };
    }

    return {
      totalStudy: totalMinutes,
      subjectsCovered: subjectSet.size,
      avgMinPerSubject: subjectSet.size === 0 ? 0 : Math.round(totalMinutes / subjectSet.size),
      label: isTeacher ? "Selected student" : "Your data",
    };
  }, [filteredData, isTeacher, selectedStudentId, filteredStudentsList.length, selectedBatch]);

  // ─── Chart data ───
  const labels = useMemo(() => getLabels(filterMode, refDate), [filterMode, refDate]);
  const studentCount = isTeacher && selectedStudentId === "all" ? filteredStudentsList.length : 0;

  const barChartData = useMemo(() => {
    const buckets = labels.map((label) => ({ label }));
    filteredData.forEach((item) => {
      const idx = getBucketIndex(item, filterMode);
      if (idx >= 0 && idx < buckets.length && item.subject) {
        buckets[idx][item.subject] = (buckets[idx][item.subject] || 0) + (item.minutesUsed || 0);
      }
    });
    if (studentCount > 0) {
      buckets.forEach((bucket) => {
        subjects.forEach((s) => {
          if (bucket[s]) bucket[s] = Math.round(bucket[s] / studentCount);
        });
      });
    }
    return buckets;
  }, [filteredData, filterMode, labels, studentCount, subjects]);

  const currentUserClass = user?.studentClass || user?.class;

  const pieChartData = useMemo(() => {
    const subjectsMap = {};

    // Quick student class lookup map for teachers
    const studentClassMap = {};
    if (isTeacher) {
      students.forEach(s => {
        const id = s.penId || s.penid;
        if (id) {
          studentClassMap[id] = s.studentClass || s.class;
        }
      });
    }

    filteredData.forEach((item) => {
      if (!item.subject) return;
      if (!subjectsMap[item.subject]) {
        subjectsMap[item.subject] = { value: 0, chapters: {} };
      }
      subjectsMap[item.subject].value += (item.minutesUsed || 0);

      let chap = item.chapter || "Uncategorized";
      if (chap !== "Uncategorized") {
        const sClass = isTeacher ? studentClassMap[item.penId] : currentUserClass;
        chap = getChapterName(sClass, item.subject, chap, subjectData);
      }

      subjectsMap[item.subject].chapters[chap] = (subjectsMap[item.subject].chapters[chap] || 0) + (item.minutesUsed || 0);
    });

    if (studentCount > 0) {
      Object.keys(subjectsMap).forEach((s) => {
        subjectsMap[s].value = Math.round(subjectsMap[s].value / studentCount);
        Object.keys(subjectsMap[s].chapters).forEach(c => {
          subjectsMap[s].chapters[c] = Math.round(subjectsMap[s].chapters[c] / studentCount);
        });
      });
    }

    const grandTotal = Object.values(subjectsMap).reduce((a, b) => a + b.value, 0);
    return Object.entries(subjectsMap).map(([name, data]) => ({
      name,
      value: data.value,
      chapters: data.chapters,
      total: grandTotal
    }));
  }, [filteredData, studentCount, subjectData, isTeacher, students, currentUserClass]);

  // ─── Treemap Data ───
  const treemapData = useMemo(() => {
    const subjectsMap = {};
    const studentClassMap = {};
    if (isTeacher) {
      students.forEach(s => {
        const id = s.penId || s.penid;
        if (id) studentClassMap[id] = s.studentClass || s.class;
      });
    }

    filteredData.forEach((item) => {
      if (!item.subject) return;
      if (!subjectsMap[item.subject]) {
        subjectsMap[item.subject] = { value: 0, chapters: {} };
      }
      subjectsMap[item.subject].value += (item.minutesUsed || 0);

      let chap = item.chapter || "Uncategorized";
      if (chap !== "Uncategorized") {
        const sClass = isTeacher ? studentClassMap[item.penId] : currentUserClass;
        chap = getChapterName(sClass, item.subject, chap, subjectData);
      }

      subjectsMap[item.subject].chapters[chap] = (subjectsMap[item.subject].chapters[chap] || 0) + (item.minutesUsed || 0);
    });

    if (studentCount > 0) {
      Object.keys(subjectsMap).forEach((s) => {
        Object.keys(subjectsMap[s].chapters).forEach(c => {
          subjectsMap[s].chapters[c] = Math.round(subjectsMap[s].chapters[c] / studentCount);
        });
      });
    }

    return Object.entries(subjectsMap).map(([subject, data]) => ({
      name: subject,
      size: data.value,
      children: Object.entries(data.chapters)
        .filter(([_, val]) => val > 0)
        .map(([chap, val]) => ({
          name: chap,
          size: val,
          subject: subject
        }))
    }));
  }, [filteredData, studentCount, subjectData, isTeacher, students, currentUserClass]);

  // ─── Heatmap Data ───
  const heatmapData = useMemo(() => {
    if (!activeData || activeData.length === 0) return { grid: [], maxVal: 1, hasData: false, months: [] };

    const today = new Date();
    const normalizedToday = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const dow = normalizedToday.getUTCDay();
    const totalDays = 52 * 7 + dow + 1;
    const msPerDay = 24 * 60 * 60 * 1000;

    const dataMap = {};
    activeData.forEach(item => {
      const d = new Date((item.dayStartS || 0) * 1000);
      const key = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
      dataMap[key] = (dataMap[key] || 0) + (item.minutesUsed || 0);
    });

    const grid = [];
    let maxVal = 1;

    for (let i = totalDays - 1; i >= 0; i--) {
      const t = normalizedToday.getTime() - i * msPerDay;
      const val = dataMap[t] || 0;
      if (val > maxVal) maxVal = val;
      grid.push({
        date: new Date(t),
        value: val
      });
    }

    const emptySlotsAtEnd = 6 - dow;
    for (let i = 0; i < emptySlotsAtEnd; i++) {
      grid.push({ empty: true });
    }

    const months = [];
    let lastMonth = -1;
    grid.forEach((cell, i) => {
      if (cell.empty) return;
      if (i % 7 === 0) {
        const m = cell.date.getUTCMonth();
        if (m !== lastMonth) {
          months.push({ label: formatUTCDate(cell.date, { month: 'short' }), colIndex: i / 7 });
          lastMonth = m;
        }
      }
    });

    return { grid, maxVal, months, hasData: true };
  }, [activeData]);

  // ─── AI Weekly Insights Data ───
  const weeklyInsights = useMemo(() => {
    if (!activeData || activeData.length === 0) return null;

    // Compare the calendar week of refDate with the previous calendar week.
    const currentWeekStart = startOfWeek(refDate);
    const prevWeekStart = new Date(currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);

    // Convert to epochs
    const currentStartEpoch = Math.floor(currentWeekStart.getTime() / 1000);
    const currentEndEpoch = currentStartEpoch + 7 * 24 * 60 * 60;

    const prevStartEpoch = Math.floor(prevWeekStart.getTime() / 1000);
    const prevEndEpoch = prevStartEpoch + 7 * 24 * 60 * 60;

    let thisWeekMin = 0;
    let lastWeekMin = 0;
    let topSubjectThisWeek = {};

    activeData.forEach(item => {
      const d = item.dayStartS || 0;
      if (d >= currentStartEpoch && d < currentEndEpoch) {
        thisWeekMin += (item.minutesUsed || 0);
        if (item.subject) {
          topSubjectThisWeek[item.subject] = (topSubjectThisWeek[item.subject] || 0) + (item.minutesUsed || 0);
        }
      } else if (d >= prevStartEpoch && d < prevEndEpoch) {
        lastWeekMin += (item.minutesUsed || 0);
      }
    });

    const isAllStudents = isTeacher && selectedStudentId === "all";

    // If "All Students" is selected, average the totals
    if (isAllStudents && students.length > 0) {
      thisWeekMin = Math.round(thisWeekMin / students.length);
      lastWeekMin = Math.round(lastWeekMin / students.length);
    }

    let subjectMinList = Object.entries(topSubjectThisWeek).sort((a, b) => a[1] - b[1]);
    let favSubj = subjectMinList.length > 0 ? subjectMinList[subjectMinList.length - 1] : null;
    let leastSubj = subjectMinList.length > 0 ? subjectMinList[0][0] : null;

    let message = "";
    let actionableSuggestion = "";
    const entityName = isAllStudents ? "Your class average" : "You";

    if (thisWeekMin === 0) {
      message = isAllStudents
        ? "No study time logged by students for this week. It might be time to assign some practice!"
        : "You haven't logged any study time this week. Try starting with a 15-minute session today!";
      actionableSuggestion = isAllStudents
        ? "Action: Send a quick reminder announcement to your class to log their first session."
        : "Action: Set a 15-minute timer and start with your favorite subject right now.";
    } else if (lastWeekMin === 0) {
      message = isAllStudents
        ? `Great start! Your class average studied for ${thisWeekMin} minutes this week. Keep up the momentum!`
        : `Great start! You've studied for ${thisWeekMin} minutes this week. Keep up the momentum!`;
      actionableSuggestion = isAllStudents
        ? "Action: Review the 'At-Risk' list below and reach out to students who haven't started."
        : `Action: Keep the momentum going! Try a quick AI-generated quiz on ${favSubj ? favSubj[0] : 'your recent topics'}.`;
    } else {
      const diff = thisWeekMin - lastWeekMin;
      const pct = Math.abs(Math.round((diff / lastWeekMin) * 100));
      if (diff > 0) {
        message = isAllStudents
          ? `Excellent progress! Your class average is up ${pct}% in study time compared to last week, with a strong focus on ${favSubj ? favSubj[0] : 'core subjects'}.`
          : `Excellent progress! You're up ${pct}% in study time compared to last week, with a strong focus on ${favSubj ? favSubj[0] : 'your subjects'}.`;
      } else if (diff < 0) {
        message = isAllStudents
          ? `Class study time is down by ${pct}% compared to last week average. Consider reminding students to cover ${favSubj ? favSubj[0] : 'their assignments'}.`
          : `Your study time is down by ${pct}% compared to last week. Consider carving out an extra hour this weekend for ${favSubj ? favSubj[0] : 'review'}.`;
      } else {
        message = `Consistent effort! ${entityName} matched the study time from last week perfectly.`;
      }

      if (leastSubj && subjectMinList.length > 1 && diff <= 0) {
        actionableSuggestion = isAllStudents
          ? `Action: Focus this week's assignments on ${leastSubj} to balance out the class average.`
          : `Action: You spent the least time on ${leastSubj}. Dedicate your next study block to catching up on it.`;
      } else {
        actionableSuggestion = isAllStudents
          ? "Action: Review the 'At-Risk' list below and reach out to students who are falling behind."
          : `Action: Keep the momentum going! Try a quick AI-generated quiz on ${favSubj ? favSubj[0] : 'your recent topics'}.`;
      }
    }

    return {
      message,
      actionableSuggestion,
      thisWeek: thisWeekMin,
      lastWeek: lastWeekMin,
      topSubject: favSubj ? favSubj[0] : "None"
    };
  }, [activeData, refDate, isTeacher, selectedStudentId, students]);

  // ─── Student grid data (teacher only) ───
  const gridData = useMemo(() => {
    if (!isTeacher || !allData) return [];

    const studentMap = {};
    allData
      .filter((d) => d.minutesUsed > 0)
      .forEach((item) => {
        const dayStart = item.dayStartS || 0;
        if (dayStart === 0 || dayStart < startEpoch || dayStart >= endEpoch) return;
        if (!studentMap[item.penId]) {
          studentMap[item.penId] = { penId: item.penId, totalTime: 0, subjects: {} };
        }
        studentMap[item.penId].totalTime += item.minutesUsed || 0;
        studentMap[item.penId].subjects[item.subject] =
          (studentMap[item.penId].subjects[item.subject] || 0) + (item.minutesUsed || 0);
      });

    let result = students.map((s) => {
      const d = studentMap[s.penId] || { totalTime: 0, subjects: {} };
      return {
        penId: s.penId,
        name: s.email,
        totalTime: d.totalTime,
        subjects: Object.entries(d.subjects).map(([name, time]) => ({
          name,
          time,
          color: subjectColorMap[name] || "#6366f1",
        })),
      };
    });

    result.sort((a, b) => b.totalTime - a.totalTime);

    if (gridFilter === "best10") {
      const count = Math.max(1, Math.ceil(result.length * 0.1));
      result = result.slice(0, count);
    } else if (gridFilter === "worst10") {
      const count = Math.max(1, Math.ceil(result.length * 0.1));
      result = result.slice(-count);
    }

    return result;
  }, [isTeacher, allData, students, startEpoch, endEpoch, subjectColorMap, gridFilter]);

  const maxGridTime = useMemo(
    () => gridData.reduce((max, s) => Math.max(max, s.totalTime), 0),
    [gridData]
  );

  // ─── Teacher Insights (At-Risk & Improvement) ───
  const teacherInsights = useMemo(() => {
    if (!isTeacher || !allData || students.length === 0) return { atRisk: [], improvers: [] };

    const currentWeekStart = startOfWeek(refDate);
    const prevWeekStart = new Date(currentWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    const currentStartEpoch = Math.floor(currentWeekStart.getTime() / 1000);
    const currentEndEpoch = currentStartEpoch + 7 * 24 * 60 * 60;
    const prevStartEpoch = Math.floor(prevWeekStart.getTime() / 1000);
    const prevEndEpoch = prevStartEpoch + 7 * 24 * 60 * 60;

    const studentStats = {};

    students.forEach(s => {
      studentStats[s.penId] = {
        name: s.email || s.name || s.penId,
        thisWeek: 0,
        lastWeek: 0
      };
    });

    allData.forEach(item => {
      if (!studentStats[item.penId]) return;
      const d = item.dayStartS || 0;
      if (d >= currentStartEpoch && d < currentEndEpoch) {
        studentStats[item.penId].thisWeek += (item.minutesUsed || 0);
      } else if (d >= prevStartEpoch && d < prevEndEpoch) {
        studentStats[item.penId].lastWeek += (item.minutesUsed || 0);
      }
    });

    const atRisk = [];
    const improvers = [];

    Object.values(studentStats).forEach(stat => {
      const diff = stat.thisWeek - stat.lastWeek;

      if (stat.thisWeek === 0 && stat.lastWeek > 0) {
        atRisk.push({ name: stat.name, reason: "0 minutes studied this week", severity: "high" });
      } else if (stat.thisWeek === 0 && stat.lastWeek === 0) {
        atRisk.push({ name: stat.name, reason: "Inactive for over 2 weeks", severity: "critical" });
      } else if (stat.lastWeek > 30 && stat.thisWeek < stat.lastWeek * 0.5) {
        const drop = Math.round(((stat.lastWeek - stat.thisWeek) / stat.lastWeek) * 100);
        atRisk.push({ name: stat.name, reason: `Dropped by ${drop}%`, severity: "medium" });
      }

      if (stat.lastWeek >= 20 && diff > 0) {
        const pct = Math.round((diff / stat.lastWeek) * 100);
        improvers.push({ name: stat.name, improvement: pct, diffMins: diff, thisWeek: stat.thisWeek });
      } else if (stat.lastWeek < 20 && stat.thisWeek >= 60) {
        improvers.push({ name: stat.name, improvement: 999, diffMins: stat.thisWeek, thisWeek: stat.thisWeek });
      }
    });

    return {
      atRisk: atRisk.sort((a, b) => {
        const map = { critical: 3, high: 2, medium: 1 };
        return map[b.severity] - map[a.severity];
      }).slice(0, 5),
      improvers: improvers.sort((a, b) => b.improvement - a.improvement).slice(0, 5)
    };
  }, [allData, isTeacher, students, refDate]);

  const canGoNext = useMemo(
    () => canNavigateDate(filterMode, refDate, "next"),
    [filterMode, refDate]
  );

  // ─── Handlers ───
  const handlePrev = () => setRefDate((d) => navigateDate(filterMode, d, "prev"));
  const handleNext = () => setRefDate((d) => navigateDate(filterMode, d, "next"));
  const handleFilterModeChange = (mode) => {
    setRefDate((d) => resolveRefDateForModeChange(filterMode, mode, d, activeData));
    setFilterMode(mode);
  };
  const handleStudentSelect = (id) => setSelectedStudentId(id);
  const handleLogout = () => {
    localStorage.removeItem("user");
    window.location.href = "/";
  };
  const handleGridStudentClick = (id) => {
    setModalStudentId(id);
    setIsProfileModalOpen(true);
  };

  const handleExportCSV = () => {
    if (!teacherInsights) return;
    
    let csvContent = "data:text/csv;charset=utf-8,";
    csvContent += "Report Type,Student Name,Details,Metric\n";
    
    teacherInsights.atRisk.forEach(s => {
      csvContent += `"At-Risk","${s.name}","${s.reason}","${s.severity}"\n`;
    });
    
    teacherInsights.improvers.forEach(s => {
      csvContent += `"Improvers","${s.name}","+${s.improvement}%","${s.thisWeek} min this week"\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Teacher_Insights_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Auto-scroll calendar to the rightmost side
  useEffect(() => {
    if (heatmapData?.hasData && calendarScrollRef.current) {
      calendarScrollRef.current.scrollLeft = calendarScrollRef.current.scrollWidth;
    }
  }, [heatmapData]);

  // ─── Loading ───
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  const navLabel = getNavigationLabel(filterMode, refDate);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-sky-500 to-emerald-400" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* ── HEADER ── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] text-slate-400 uppercase">
              Overview
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight">
              {isTeacher ? "Institute Analytics" : "Study Analytics"}
            </h1>
            <p className="mt-2 text-sm text-slate-400 max-w-xl">
              {isTeacher
                ? "Monitor student performance, compare study habits, and identify who needs attention."
                : "A high-level view of your learning performance, subject focus, and AI-assisted study sessions."}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => load()}
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-800 hover:border-slate-600 transition-colors"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Refresh
            </button>
            {user?.email && (
              <div ref={accountMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setIsAccountMenuOpen((open) => !open)}
                  className="flex items-center gap-3 rounded-full border border-slate-800 bg-slate-900/70 px-3 py-2 text-left transition-colors hover:border-slate-700 hover:bg-slate-900 min-w-0"
                >
                  <div className="flex shrink-0 h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-sky-400 text-xs font-semibold text-slate-950">
                    {(user.name || user.email)
                      .split(/[\s@]/)
                      .map((n) => n[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                  <div className="text-xs min-w-0">
                    <p className="font-medium text-slate-100 leading-none truncate">
                      {user.name || user.email}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 truncate">
                      {isTeacher ? "Institute" : "Active learner"}
                    </p>
                  </div>
                  <span className="text-xs text-slate-500 shrink-0">{isAccountMenuOpen ? "▲" : "▼"}</span>
                </button>

                {isAccountMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+10px)] z-50 min-w-[200px] rounded-2xl border border-slate-800 bg-slate-950/95 p-2 shadow-[0_18px_45px_rgba(15,23,42,0.75)] backdrop-blur gap-1 flex flex-col">
                    {!isTeacher && (
                      <button
                        type="button"
                        onClick={() => { window.location.href = "/student-info"; }}
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-sky-200 transition-colors hover:bg-sky-500/10 hover:text-sky-100"
                      >
                        <span>Student Profile</span>
                        <span className="text-xs uppercase tracking-[0.18em] text-sky-300">Edit</span>
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsAccountMenuOpen(false);
                        setIsChangePasswordOpen(true);
                      }}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-slate-200 transition-colors hover:bg-slate-800 hover:text-slate-100"
                    >
                      <span>Security</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-slate-400">Password</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-rose-200 transition-colors hover:bg-rose-500/10 hover:text-rose-100"
                    >
                      <span>Logout</span>
                      <span className="text-xs uppercase tracking-[0.18em] text-rose-300">Exit</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── FILTER BAR ── */}
        <div ref={filterBarRef} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4">
          <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 w-full">
            {/* Prev button */}
            <button
              onClick={handlePrev}
              className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs sm:text-sm font-medium hover:bg-slate-700 transition-colors"
            >
              &#9664; Prev
            </button>

            {/* Filter mode toggle */}
            <div className="flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950/60 p-0.5 min-w-0">
              {FILTER_MODES.map((f) => (
                <button
                  key={f.key}
                  onClick={() => handleFilterModeChange(f.key)}
                  className={`rounded-full px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-colors ${filterMode === f.key
                    ? "bg-indigo-500 text-white"
                    : "text-slate-400 hover:text-slate-100"
                    }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {/* Navigation label */}
            <span className="min-w-0 flex-1 text-center text-xs sm:text-sm font-medium text-slate-200 px-1">
              {navLabel}
            </span>

            {/* Next button */}
            <button
              onClick={handleNext}
              disabled={!canGoNext}
              className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-slate-800"
            >
              Next &#9654;
            </button>

            {/* Batch and Student dropdowns (teacher only) */}
            {isTeacher && (
              <div className="flex w-full flex-wrap items-center gap-2 lg:ml-auto lg:w-auto">
                {uniqueBatches.length > 1 && (
                  <select
                    value={selectedBatch}
                    onChange={(e) => setSelectedBatch(e.target.value)}
                    className="min-w-0 flex-1 lg:flex-none lg:w-[220px] rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs sm:text-sm text-slate-100 outline-none focus:border-indigo-500 transition-colors cursor-pointer"
                  >
                    <option value="all">All Batches</option>
                    {uniqueBatches.filter(b => b !== "all").map((b) => (
                      <option key={b} value={b}>Batch: {b}</option>
                    ))}
                  </select>
                )}
                
                <select
                  value={selectedStudentId}
                  onChange={(e) => handleStudentSelect(e.target.value)}
                  className="min-w-0 flex-1 lg:flex-none lg:w-[240px] rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs sm:text-sm text-slate-100 outline-none focus:border-indigo-500 transition-colors cursor-pointer"
                >
                  <option value="all">All Students (Average)</option>
                  {filteredStudentsList.map((s) => (
                    <option key={s.penId} value={s.penId}>
                      {s.name || s.email}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        {/* ── STATS CARDS ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-950 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.85)]">
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-indigo-500/10 blur-2xl" />
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Total study time
            </p>
            <div className="mt-4">
              <p className="text-4xl font-semibold tabular-nums">
                {stats.totalStudy}
                <span className="ml-1 text-sm font-normal text-slate-400">min</span>
              </p>
              <p className="mt-1 text-xs text-slate-500">{stats.label}</p>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
            <div className="absolute -left-6 -bottom-10 h-28 w-28 rounded-full bg-sky-500/10 blur-2xl" />
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Subjects covered
            </p>
            <div className="mt-4">
              <p className="text-4xl font-semibold tabular-nums">{stats.subjectsCovered}</p>
              <p className="mt-1 text-xs text-slate-500">{stats.label}</p>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950/80 p-5 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
            <div className="absolute right-0 bottom-0 h-24 w-24 rounded-full bg-emerald-500/10 blur-2xl" />
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Avg. minutes / subject
            </p>
            <div className="mt-4">
              <p className="text-4xl font-semibold tabular-nums">
                {stats.avgMinPerSubject}
                <span className="ml-1 text-sm font-normal text-slate-400">min</span>
              </p>
              <p className="mt-1 text-xs text-slate-500">{stats.label}</p>
            </div>
          </div>
        </div>

        {/* ── CHARTS ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Subjects
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-50">Time Breakdown</h2>
            </div>
            <div className="h-72 dashboard-chart">
              {filteredData.length === 0 ? (
                <EmptyState title="No Time Tracked" message="You haven't logged any study sessions in this period." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData} accessibilityLayer={false}>
                    <XAxis
                      dataKey="label"
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#9ca3af", fontSize: 11 }}
                    />
                    <YAxis
                      tickLine={false}
                      axisLine={false}
                      tick={{ fill: "#9ca3af", fontSize: 11 }}
                    />
                    <Tooltip
                      content={<CustomBarTooltip />}
                      cursor={false}
                    />
                    {subjects.map((subject) => (
                      <Bar
                        key={subject}
                        dataKey={subject}
                        stackId="stack"
                        fill={subjectColorMap[subject]}
                        activeBar={false}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {subjects.map((s) => (
                <div key={s} className="flex items-center gap-1.5 text-xs text-slate-400">
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: subjectColorMap[s] }}
                  />
                  {s}
                </div>
              ))}
            </div>
          </div>

          {/* Pie / Donut Chart */}
          <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                Distribution
              </p>
              <h2 className="mt-1 text-lg font-semibold text-slate-50">Subject Overview</h2>
            </div>
            <div className="h-72 dashboard-chart">
              {pieChartData.length === 0 ? (
                <EmptyState title="No Subjects" message="Start learning a new subject to see your breakdown." />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart accessibilityLayer={false}>
                    <Pie
                      data={pieChartData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={110}
                      paddingAngle={4}
                      activeShape={false}
                    >
                      {pieChartData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={subjectColorMap[entry.name] || "#6366f1"}
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="mt-4 flex flex-wrap gap-3">
              {pieChartData.map((entry) => (
                <div
                  key={entry.name}
                  className="flex items-center gap-1.5 text-xs text-slate-400"
                >
                  <div
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: subjectColorMap[entry.name] }}
                  />
                  {entry.name} ({entry.value} min)
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── AI ASSISTANT ── */}
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.85)]">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
            AI assistant
          </p>
          <h2 className="mt-2 text-lg font-semibold text-slate-50">
            Ask about anything you&apos;re studying
          </h2>
          <p className="mt-2 text-xs text-slate-400">
            Use the AI search to clarify concepts, generate practice questions, or summarize topics
            directly from your dashboard.
          </p>
          <div className="mt-4 rounded-xl border border-slate-800 bg-slate-950/80 p-3">
            <AISearch penId={penId} />
          </div>
        </div>

        {/* ── STUDENT GRID (Teacher only) ── */}
        {isTeacher && (
          <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
                  Students
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-50">Practice Overview</h2>
              </div>
              <div className="flex items-center gap-1 rounded-full border border-slate-700 bg-slate-900/60 p-0.5">
                {GRID_FILTERS.map((f) => (
                  <button
                    key={f.key}
                    onClick={() => setGridFilter(f.key)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${gridFilter === f.key
                      ? "bg-indigo-500 text-white"
                      : "text-slate-400 hover:text-slate-100"
                      }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {gridData.length === 0 ? (
              <div className="py-8">
                <EmptyState title="No Students Active" message="No one has tracked any practice time in this period." />
              </div>
            ) : (
              <div className="space-y-2">
                {/* Grid header */}
                <div className="hidden sm:grid grid-cols-[minmax(140px,1fr)_3fr_80px] gap-4 px-4 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">
                  <span>Student</span>
                  <span>Practice Time</span>
                  <span className="text-right">Total</span>
                </div>

                {gridData.map((student) => (
                  <div
                    key={student.penId}
                    onClick={() => handleGridStudentClick(student.penId)}
                    className={`grid grid-cols-1 sm:grid-cols-[minmax(140px,1fr)_3fr_80px] gap-2 sm:gap-4 items-center px-4 py-3 rounded-xl cursor-pointer transition-colors ${selectedStudentId === student.penId
                      ? "bg-indigo-500/10 border border-indigo-500/30"
                      : "hover:bg-slate-800/50 border border-transparent"
                      }`}
                  >
                    <span className="text-sm font-medium text-indigo-300 truncate hover:text-indigo-200">
                      {student.name}
                    </span>

                    {/* Progress bar */}
                    <div
                      className="relative"
                      onMouseEnter={() => setHoveredGridStudent(student.penId)}
                      onMouseLeave={() => setHoveredGridStudent(null)}
                    >
                      <div className="h-6 rounded-full bg-slate-800 overflow-hidden flex">
                        {maxGridTime > 0 &&
                          student.subjects.map((sub) => (
                            <div
                              key={sub.name}
                              className="h-full transition-all"
                              style={{
                                width: `${(sub.time / maxGridTime) * 100}%`,
                                backgroundColor: sub.color,
                                minWidth: sub.time > 0 ? "4px" : "0",
                              }}
                            />
                          ))}
                      </div>
                      {hoveredGridStudent === student.penId && student.subjects.length > 0 && (
                        <GridSegmentTooltip subjects={student.subjects} totalTime={student.totalTime} />
                      )}
                    </div>

                    <span className="text-sm font-medium text-slate-300 text-right tabular-nums">
                      {student.totalTime} min
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TEACHER "AT-RISK" & "IMPROVERS" ── */}
        {isTeacher && (
          <div className="space-y-6 pt-6 mt-8 border-t border-slate-800">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-white mb-1">
                  Teacher Insights
                </h2>
                <p className="text-sm text-slate-400">
                  Automated alerts for students needing attention and those improving rapidly.
                </p>
              </div>
              <button
                onClick={handleExportCSV}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-sm font-medium text-slate-200 transition-colors hover:bg-slate-700 hover:text-white shadow-sm whitespace-nowrap"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                Export CSV Report
              </button>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* At-Risk Panel */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-rose-500/20 text-rose-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-rose-100">At-Risk Alerts</h3>
                  <p className="text-xs text-rose-300/70">Students needing immediate attention</p>
                </div>
              </div>
              {teacherInsights.atRisk.length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-800 rounded-xl">All students on track!</div>
              ) : (
                <div className="space-y-3">
                  {teacherInsights.atRisk.map((s, i) => (
                    <div key={i} className="flex flex-col sm:flex-row justify-between sm:items-center bg-slate-900/60 p-3 rounded-xl border border-slate-800/50 gap-2">
                      <span className="text-sm font-medium text-slate-200">{s.name}</span>
                      <span className={`text-xs px-2 py-1 rounded-md font-medium w-fit ${s.severity === 'critical' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/20' : s.severity === 'high' ? 'bg-orange-500/20 text-orange-300 border border-orange-500/20' : 'bg-amber-500/10 text-amber-300 border border-amber-500/10'}`}>{s.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Improvers Panel */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-400">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-emerald-100">Improvement Velocity</h3>
                  <p className="text-xs text-emerald-300/70">Top week-over-week climbers</p>
                </div>
              </div>
              {teacherInsights.improvers.length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-800 rounded-xl">Not enough study data to compute climbers yet.</div>
              ) : (
                <div className="space-y-3">
                  {teacherInsights.improvers.map((s, i) => (
                    <div key={i} className="flex justify-between items-center bg-slate-900/60 p-3 rounded-xl border border-slate-800/50">
                      <span className="text-sm font-medium text-slate-200 flex items-center gap-2">
                        {i === 0 ? '🏆' : i === 1 ? '🥈' : i === 2 ? '🥉' : '✨'} <span className="truncate max-w-[120px] sm:max-w-[200px]">{s.name}</span>
                      </span>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-[10px] text-slate-400 leading-none">{s.thisWeek} min this week</span>
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-2 py-1 rounded-md font-medium flex items-center gap-1 border border-emerald-500/20 leading-none">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m18 15-6-6-6 6" /></svg>
                          {s.improvement === 999 ? 'New peak active' : `+${s.improvement}%`}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {/* ── AI WEEKLY INSIGHTS ── */}
        {weeklyInsights && (
          <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)] relative overflow-hidden mt-8">
            <div className="absolute top-0 right-0 p-3 opacity-20">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
            </div>
            <div className="flex items-start gap-4">
              <div className="mt-1 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-indigo-500/20 text-indigo-400">
                <span className="text-xl">✨</span>
              </div>
              <div className="relative z-10">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-indigo-400">
                  AI-Generated Insight
                </p>
                <h3 className="mt-1 text-lg font-semibold text-slate-50">Weekly Summary</h3>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-3xl">
                  {weeklyInsights.message}
                </p>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-indigo-500/10 px-3 py-2 border border-indigo-500/20">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500/20 text-[10px]">💡</span>
                  <span className="text-xs font-medium text-indigo-200">{weeklyInsights.actionableSuggestion}</span>
                </div>
                <div className="mt-4 flex gap-4 text-xs font-medium text-slate-400">
                  <span className="bg-slate-900/50 rounded-md px-2 py-1 border border-slate-800">This Week: <span className="text-slate-200">{weeklyInsights.thisWeek} min</span></span>
                  <span className="bg-slate-900/50 rounded-md px-2 py-1 border border-slate-800">Last Week: <span className="text-slate-200">{weeklyInsights.lastWeek} min</span></span>
                  {weeklyInsights.topSubject !== "None" && (
                    <span className="bg-slate-900/50 rounded-md px-2 py-1 border border-slate-800">Top Focus: <span className="text-indigo-300">{weeklyInsights.topSubject}</span></span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── ADVANCED VISUALIZATIONS ── */}
        <div className="space-y-6 pt-6 mt-8 border-t border-slate-800">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-white mb-1">
              Advanced Visualizations
            </h2>
            <p className="text-sm text-slate-400">
              Deeper analytics on learning consistency and chapter-level focus.
            </p>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* Activity Heatmap */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)] flex flex-col items-center xl:items-start overflow-hidden">
              <div className="mb-6 w-full text-center xl:text-left">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400 mb-1">Consistency</p>
                <h3 className="text-lg font-semibold text-slate-50">Activity Calendar</h3>
              </div>

              {!heatmapData.hasData ? (
                <div className="w-full">
                  <EmptyState title="No Activity Calendar" message="Study consistently to build your activity heatmap." />
                </div>
              ) : (
                <div 
                  ref={calendarScrollRef}
                  className="w-full max-w-[800px] overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-slate-700"
                >
                  <div className="min-w-max">
                    <div className="flex gap-2 relative mt-4">
                      {/* Days of week labels */}
                      <div className="flex flex-col gap-[3px] text-[9px] text-slate-500 font-medium pr-1" style={{ height: '95px' }}>
                        <div className="h-[11px] flex items-center">Sun</div>
                        <div className="h-[11px]"></div>
                        <div className="h-[11px] flex items-center">Tue</div>
                        <div className="h-[11px]"></div>
                        <div className="h-[11px] flex items-center">Thu</div>
                        <div className="h-[11px]"></div>
                        <div className="h-[11px] flex items-center">Sat</div>
                      </div>

                      {/* Calendar Grid */}
                      <div className="relative">
                        {/* Month labels positioned relative to columns */}
                        <div className="flex absolute -top-5 left-0 w-full h-4 text-[10px] text-slate-500">
                          {heatmapData.months.map((m, idx) => (
                            <span key={idx} style={{ position: 'absolute', left: `${m.colIndex * 14}px` }}>
                              {m.label}
                            </span>
                          ))}
                        </div>

                        <div
                          className="flex flex-col flex-wrap gap-[3px] overflow-x-auto overflow-y-hidden"
                          style={{ height: '95px' }}
                        >
                          {heatmapData.grid.map((cell, i) => {
                            if (cell.empty) {
                              return <div key={`empty-${i}`} className="w-[11px] h-[11px] rounded-[2px] bg-transparent" />;
                            }
                            const intensity = cell.value === 0 ? 0.05 : Math.max(0.2, cell.value / heatmapData.maxVal);
                            return (
                              <div
                                key={`cell-${i}`}
                                className="w-[11px] h-[11px] rounded-[2px] transition-all hover:ring-2 hover:ring-slate-300 hover:scale-110 z-10"
                                style={{
                                  backgroundColor: cell.value === 0 ? '#1e293b' : '#10b981',
                                  opacity: cell.value === 0 ? 1 : intensity + 0.1
                                }}
                                onMouseEnter={(e) => {
                                  setHoveredCell({
                                    date: formatUTCDate(cell.date, { month: 'short', day: 'numeric', year: 'numeric' }),
                                    value: cell.value,
                                    x: e.clientX,
                                    y: e.clientY
                                  });
                                }}
                                onMouseMove={(e) => {
                                  setHoveredCell(prev => prev ? { ...prev, x: e.clientX, y: e.clientY } : null);
                                }}
                                onMouseLeave={() => setHoveredCell(null)}
                              />
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Hierarchical Treemap */}
            <div className="rounded-2xl border border-slate-800 bg-slate-950/80 p-6 shadow-[0_18px_45px_rgba(15,23,42,0.75)]">
              <div className="mb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Chapters</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-50">Topic Drill-down</h3>
              </div>
              <div className="h-72 w-full dashboard-chart relative">
                {treemapData.length === 0 ? (
                  <EmptyState title="No Chapter Data" message="Dive into specific topics to visualize your focus here." />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <Treemap
                      data={treemapData}
                      dataKey="size"
                      ratio={4 / 3}
                      stroke="#0f172a"
                      fill="#6366f1"
                      content={(props) => <TreemapContent {...props} colorMap={subjectColorMap} />}
                      isAnimationActive={false}
                    >
                      <Tooltip content={(props) => <CustomTreemapTooltip {...props} colorMap={subjectColorMap} />} />
                    </Treemap>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

          </div>
        </div>

        {/* Heatmap Custom Tooltip floating */}
        {hoveredCell && (
          <div
            style={{
              ...TOOLTIP_STYLE,
              position: 'fixed',
              left: hoveredCell.x + 15,
              top: hoveredCell.y - 15,
              zIndex: 9999,
              pointerEvents: 'none'
            }}
          >
            <p style={{ margin: "0 0 4px", fontSize: 13, fontWeight: 600 }}>{hoveredCell.date}</p>
            <p style={{ margin: 0, fontSize: 12, color: "#d1d5db" }}>{hoveredCell.value} min studied</p>
          </div>
        )}

        <StudentProfileModal 
          isOpen={isProfileModalOpen} 
          onClose={() => setIsProfileModalOpen(false)}
          studentId={modalStudentId}
          studentName={isTeacher && students ? (students.find(s => s.penId === modalStudentId)?.email || modalStudentId) : "Student"}
          allData={filteredData}
          subjectColorMap={subjectColorMap}
          getChapterName={(subject, code) => {
            const sClass = isTeacher ? (students.find(s => s.penId === modalStudentId)?.studentClass || students.find(s => s.penId === modalStudentId)?.class) : currentUserClass;
            return getChapterName(sClass, subject, code, subjectData);
          }}
        />

        <ChangePasswordModal 
          isOpen={isChangePasswordOpen} 
          onClose={() => setIsChangePasswordOpen(false)} 
          userEmail={user?.email} 
        />

      </div>
    </div>
  );
}
